/**
 * Contact Cards Component
 *
 * Displays loan team contact information:
 * - Loan Officer (primary contact)
 * - Processor (for questions)
 *
 * Features clickable email and phone links
 */

import { Mail, Phone } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import type { ConditionsPortalLoanOfficer, LoanTeamContact } from '@/types/conditions-portal';

interface ContactCardsProps {
  loanOfficer: ConditionsPortalLoanOfficer;
  processor: LoanTeamContact | null;
}

export const ContactCards = ({ loanOfficer, processor }: ContactCardsProps) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {/* Loan Officer Card */}
      <Card className="shadow-sm border-[#183C2E]">
        <CardContent className="p-5">
          <div className={`flex gap-4 ${loanOfficer.photoUrl ? 'items-center' : 'flex-col items-center text-center'}`}>
            {/* Photo */}
            {loanOfficer.photoUrl && (
              <img
                src={loanOfficer.photoUrl}
                alt={loanOfficer.name}
                className="h-20 w-20 rounded-full object-cover border-2 border-gray-100"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}

            <div className={`${loanOfficer.photoUrl ? 'flex-1 min-w-0' : ''}`}>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                Your Loan Officer
              </p>
              <p className="font-semibold text-gray-900 truncate">{loanOfficer.name}</p>
              {loanOfficer.title && (
                <p className="text-xs text-gray-500">{loanOfficer.title}</p>
              )}

              {/* Contact Info */}
              <div className={`mt-2 space-y-1 ${!loanOfficer.photoUrl ? 'flex flex-col items-center' : ''}`}>
                {loanOfficer.email && (
                  <a
                    href={`mailto:${loanOfficer.email}`}
                    className="flex items-center gap-1.5 text-sm text-[#183C2E] hover:underline"
                  >
                    <Mail className="h-3.5 w-3.5" />
                    <span className="truncate">{loanOfficer.email}</span>
                  </a>
                )}
                {(loanOfficer.phone || loanOfficer.mobilePhone) && (
                  <a
                    href={`tel:${loanOfficer.mobilePhone || loanOfficer.phone}`}
                    className="flex items-center gap-1.5 text-sm text-[#183C2E] hover:underline"
                  >
                    <Phone className="h-3.5 w-3.5" />
                    <span>{loanOfficer.mobilePhone || loanOfficer.phone}</span>
                  </a>
                )}
              </div>

              {/* NMLS */}
              {loanOfficer.nmls && (
                <p className="text-xs text-gray-400 mt-2">NMLS# {loanOfficer.nmls}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Processor Card */}
      <Card className="shadow-sm border-[#183C2E]">
        <CardContent className="p-5">
          <div className={`flex gap-4 ${processor?.photoUrl ? 'items-center' : 'flex-col items-center text-center'}`}>
            {/* Photo */}
            {processor?.photoUrl && (
              <img
                src={processor.photoUrl}
                alt={processor.name}
                className="h-20 w-20 rounded-full object-cover border-2 border-blue-100"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}

            <div className={`${processor?.photoUrl ? 'flex-1 min-w-0' : ''}`}>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                Questions?
              </p>

              {processor ? (
                <>
                  <p className="font-semibold text-gray-900 truncate">{processor.name}</p>
                  <p className="text-xs text-gray-500">Processor</p>

                  {/* Contact Info */}
                  <div className={`mt-2 space-y-1 ${!processor.photoUrl ? 'flex flex-col items-center' : ''}`}>
                    {processor.email && (
                      <a
                        href={`mailto:${processor.email}`}
                        className="flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
                      >
                        <Mail className="h-3.5 w-3.5" />
                        <span className="truncate">{processor.email}</span>
                      </a>
                    )}
                    {processor.phone && (
                      <a
                        href={`tel:${processor.phone}`}
                        className="flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
                      >
                        <Phone className="h-3.5 w-3.5" />
                        <span>{processor.phone}</span>
                      </a>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <p className="font-semibold text-gray-900">Contact Your Loan Officer</p>
                  <p className="text-sm text-gray-500 mt-1">
                    For any questions about these conditions, please reach out to your loan officer.
                  </p>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactCards;
